/**
 * Output Style Formatter
 * Formats both Final Prompt and Negative Prompt according to the selected output style.
 * This is a pure utility layer — it does NOT modify any existing logic.
 */

import type { PromptFormat } from './engineTypes';
import { analyzeScene, type SceneProfile } from './negativePromptEngine';

export type ExtendedPromptFormat = PromptFormat;

// ─── Scene Section Extraction ───

interface PromptSections {
  scene: string;
  subject: string;
  camera: string;
  lighting: string;
  style: string;
  composition: string;
  vfx: string;
  quality: string;
  other: string;
}

function extractSections(prompt: string): PromptSections {
  const sections: PromptSections = {
    scene: '', subject: '', camera: '', lighting: '',
    style: '', composition: '', vfx: '', quality: '', other: '',
  };

  // Try to detect if the prompt already has labeled sections
  const labeledMatch = prompt.match(/(?:scene|subject|camera|lighting|style|composition|vfx|quality)\s*:/i);
  if (labeledMatch) {
    // Parse labeled sections
    const sectionRegex = /(?:^|\n)\s*(scene|subject|camera|lighting|style|composition|vfx|quality)\s*:\s*(.+?)(?=\n\s*(?:scene|subject|camera|lighting|style|composition|vfx|quality)\s*:|$)/gis;
    let m;
    const matched = new Set<string>();
    while ((m = sectionRegex.exec(prompt)) !== null) {
      const key = m[1].toLowerCase() as keyof PromptSections;
      if (key in sections) {
        sections[key] = m[2].trim();
        matched.add(key);
      }
    }
    // Anything not matched goes to other
    if (matched.size > 0) return sections;
  }

  // Heuristic extraction from unstructured prompt
  const lower = prompt.toLowerCase();
  const segments = prompt.split(/[.]\s*/).map(s => s.trim()).filter(Boolean);

  for (const seg of segments) {
    const sl = seg.toLowerCase();
    if (/\b(lens|shot|camera|dslr|focal|mm lens|perspective|angle|aperture|dof|depth of field|bokeh)\b/.test(sl)) {
      sections.camera += (sections.camera ? '. ' : '') + seg;
    } else if (/\b(light|lighting|shadow|sun|glow|rim light|key light|fill|backlit|illuminat)\b/.test(sl)) {
      sections.lighting += (sections.lighting ? '. ' : '') + seg;
    } else if (/\b(style|render|photorealistic|cinematic|anime|illustration|painting|watercolor|3d)\b/.test(sl)) {
      sections.style += (sections.style ? '. ' : '') + seg;
    } else if (/\b(composit|rule of thirds|golden ratio|symmetr|framing|centered|negative space)\b/.test(sl)) {
      sections.composition += (sections.composition ? '. ' : '') + seg;
    } else if (/\b(vfx|particles|smoke|fog|haze|dust|god rays|bloom|glitch|aura|fire|lightning)\b/.test(sl)) {
      sections.vfx += (sections.vfx ? '. ' : '') + seg;
    } else if (/\b(quality|resolution|4k|8k|hdr|ultra|detailed|sharp)\b/.test(sl)) {
      sections.quality += (sections.quality ? '. ' : '') + seg;
    } else if (/\b(person|man|woman|character|subject|warrior|standing|sitting|pose|expression)\b/.test(sl)) {
      sections.subject += (sections.subject ? '. ' : '') + seg;
    } else {
      // Likely scene description
      sections.scene += (sections.scene ? '. ' : '') + seg;
    }
  }

  // If scene is empty but we have content, use the first segment
  if (!sections.scene && !sections.subject && segments.length > 0) {
    sections.scene = segments[0];
  }

  return sections;
}

// ─── Formatters ───

function formatStructured(prompt: string): string {
  const s = extractSections(prompt);
  const lines: string[] = [];
  if (s.scene) lines.push(`[Scene]: ${s.scene}`);
  if (s.subject) lines.push(`[Subject]: ${s.subject}`);
  if (s.camera) lines.push(`[Camera]: ${s.camera}`);
  if (s.lighting) lines.push(`[Lighting]: ${s.lighting}`);
  if (s.style) lines.push(`[Style]: ${s.style}`);
  if (s.composition) lines.push(`[Composition]: ${s.composition}`);
  if (s.vfx) lines.push(`[VFX]: ${s.vfx}`);
  if (s.quality) lines.push(`[Quality]: ${s.quality}`);
  if (s.other) lines.push(`[Details]: ${s.other}`);
  return lines.length ? lines.join('\n') : prompt;
}

function formatWeighted(prompt: string): string {
  const s = extractSections(prompt);
  const parts: string[] = [];
  if (s.subject) parts.push(`(${s.subject}:1.4)`);
  if (s.scene) parts.push(`(${s.scene}:1.3)`);
  if (s.lighting) parts.push(`(${s.lighting}:1.2)`);
  if (s.camera) parts.push(`(${s.camera}:1.1)`);
  if (s.style) parts.push(`(${s.style}:1.3)`);
  if (s.composition) parts.push(`(${s.composition}:1.0)`);
  if (s.vfx) parts.push(`(${s.vfx}:1.1)`);
  if (s.quality) parts.push(`(${s.quality}:1.0)`);
  if (s.other) parts.push(`(${s.other}:1.0)`);
  return parts.length ? parts.join(', ') : prompt;
}

function formatNatural(prompt: string): string {
  // Already natural for most prompts; just ensure sentence flow
  return prompt.replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ').trim();
}

function formatTags(prompt: string): string {
  // Split into comma-separated tags
  const tags = prompt
    .replace(/[.\n]+/g, ',')
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);
  return [...new Set(tags)].join(', ');
}

function formatJSON(prompt: string): string {
  const s = extractSections(prompt);
  const profile = analyzeScene(prompt);
  const obj: Record<string, unknown> = {};
  if (s.scene) obj.scene = s.scene;
  if (s.subject) obj.subject = s.subject;
  if (s.camera) obj.camera = s.camera;
  if (s.lighting) obj.lighting = s.lighting;
  if (s.style) obj.style = s.style;
  if (s.composition) obj.composition = s.composition;
  if (s.vfx) obj.vfx = s.vfx;
  if (s.quality) obj.quality = s.quality;
  if (s.other) obj.details = s.other;
  obj.detected_type = { scene: profile.sceneType, style: profile.styleType, subject: profile.subjectType };
  return JSON.stringify(obj, null, 2);
}

function formatMarkdown(prompt: string): string {
  const s = extractSections(prompt);
  const lines: string[] = ['# Prompt'];
  if (s.scene) { lines.push('## Scene', s.scene, ''); }
  if (s.subject) { lines.push('## Subject', s.subject, ''); }
  if (s.camera) { lines.push('## Camera', s.camera, ''); }
  if (s.lighting) { lines.push('## Lighting', s.lighting, ''); }
  if (s.style) { lines.push('## Style', s.style, ''); }
  if (s.composition) { lines.push('## Composition', s.composition, ''); }
  if (s.vfx) { lines.push('## VFX', s.vfx, ''); }
  if (s.quality) { lines.push('## Quality', s.quality, ''); }
  if (s.other) { lines.push('## Details', s.other, ''); }
  return lines.join('\n');
}

function formatYAML(prompt: string): string {
  const s = extractSections(prompt);
  const lines: string[] = [];
  if (s.scene) lines.push(`scene: "${s.scene}"`);
  if (s.subject) lines.push(`subject: "${s.subject}"`);
  if (s.camera) lines.push(`camera: "${s.camera}"`);
  if (s.lighting) lines.push(`lighting: "${s.lighting}"`);
  if (s.style) lines.push(`style: "${s.style}"`);
  if (s.composition) lines.push(`composition: "${s.composition}"`);
  if (s.vfx) lines.push(`vfx: "${s.vfx}"`);
  if (s.quality) lines.push(`quality: "${s.quality}"`);
  if (s.other) lines.push(`details: "${s.other}"`);
  return lines.length ? lines.join('\n') : prompt;
}

function formatGraph(prompt: string): string {
  const s = extractSections(prompt);
  const profile = analyzeScene(prompt);
  const nodes: string[] = [];
  const edges: string[] = [];
  let id = 0;

  const addNode = (label: string, value: string) => {
    if (!value) return -1;
    const nid = ++id;
    nodes.push(`[${nid}] ${label}: ${value}`);
    return nid;
  };

  const sceneId = addNode('Scene', s.scene || profile.sceneType);
  const subjectId = addNode('Subject', s.subject || profile.subjectType);
  const cameraId = addNode('Camera', s.camera);
  const lightId = addNode('Lighting', s.lighting);
  const styleId = addNode('Style', s.style || profile.styleType);
  const compId = addNode('Composition', s.composition);
  const vfxId = addNode('VFX', s.vfx);
  const qualityId = addNode('Quality', s.quality);

  // Create edges (relationships)
  if (sceneId > 0 && subjectId > 0) edges.push(`[${sceneId}] --contains--> [${subjectId}]`);
  if (cameraId > 0 && subjectId > 0) edges.push(`[${cameraId}] --frames--> [${subjectId}]`);
  if (lightId > 0 && sceneId > 0) edges.push(`[${lightId}] --illuminates--> [${sceneId}]`);
  if (styleId > 0 && sceneId > 0) edges.push(`[${styleId}] --applies_to--> [${sceneId}]`);
  if (compId > 0 && cameraId > 0) edges.push(`[${compId}] --guides--> [${cameraId}]`);
  if (vfxId > 0 && sceneId > 0) edges.push(`[${vfxId}] --enhances--> [${sceneId}]`);
  if (qualityId > 0 && sceneId > 0) edges.push(`[${qualityId}] --defines--> [${sceneId}]`);

  return `# Nodes\n${nodes.join('\n')}\n\n# Edges\n${edges.join('\n')}`;
}

function formatBullet(prompt: string): string {
  const s = extractSections(prompt);
  const lines: string[] = [];
  if (s.scene) lines.push(`• Scene: ${s.scene}`);
  if (s.subject) lines.push(`• Subject: ${s.subject}`);
  if (s.camera) lines.push(`• Camera: ${s.camera}`);
  if (s.lighting) lines.push(`• Lighting: ${s.lighting}`);
  if (s.style) lines.push(`• Style: ${s.style}`);
  if (s.composition) lines.push(`• Composition: ${s.composition}`);
  if (s.vfx) lines.push(`• VFX: ${s.vfx}`);
  if (s.quality) lines.push(`• Quality: ${s.quality}`);
  if (s.other) lines.push(`• Details: ${s.other}`);
  return lines.length ? lines.join('\n') : `• ${prompt}`;
}

function formatDSL(prompt: string): string {
  const s = extractSections(prompt);
  const lines: string[] = [];
  if (s.scene) lines.push(`scene("${s.scene}")`);
  if (s.subject) lines.push(`subject("${s.subject}")`);
  if (s.camera) lines.push(`camera("${s.camera}")`);
  if (s.lighting) lines.push(`lighting("${s.lighting}")`);
  if (s.style) lines.push(`style("${s.style}")`);
  if (s.composition) lines.push(`composition("${s.composition}")`);
  if (s.vfx) lines.push(`vfx("${s.vfx}")`);
  if (s.quality) lines.push(`quality("${s.quality}")`);
  if (s.other) lines.push(`details("${s.other}")`);
  return lines.length ? lines.join('\n') : `scene("${prompt}")`;
}

// ─── Main Formatter ───

export function formatPrompt(prompt: string, style: ExtendedPromptFormat): string {
  if (!prompt.trim()) return '';
  switch (style) {
    case 'structured': return formatStructured(prompt);
    case 'weighted': return formatWeighted(prompt);
    case 'natural': return formatNatural(prompt);
    case 'tags': return formatTags(prompt);
    case 'json': return formatJSON(prompt);
    case 'markdown': return formatMarkdown(prompt);
    case 'yaml': return formatYAML(prompt);
    case 'graph': return formatGraph(prompt);
    case 'bullet': return formatBullet(prompt);
    case 'dsl': return formatDSL(prompt);
    case 'cinematic': return formatCinematic(prompt);
    case 'stable_diffusion': return formatStableDiffusion(prompt);
    case 'midjourney': return formatMidjourney(prompt);
    case 'llm_structured': return formatLLMStructured(prompt);
    case 'node_graph': return formatNodeGraph(prompt);
    default: return prompt;
  }
}

// ─── Cinematic Formatter ───
function formatCinematic(prompt: string): string {
  // تحويل البرومبت إلى وصف سينمائي احترافي
  const s = extractSections(prompt);
  return `A cinematic scene featuring ${s.subject || 'an unknown subject'} in ${s.scene || 'an undefined environment'}, illuminated by ${s.lighting || 'cinematic lighting'}, captured with ${s.camera || 'professional camera'}, evoking a mood of ${s.style || 'cinematic style'}. Composition: ${s.composition || 'dynamic framing'}. VFX: ${s.vfx || 'subtle effects'}. Quality: ${s.quality || 'ultra detail'}.`;
}

// ─── Stable Diffusion Optimized Formatter ───
function formatStableDiffusion(prompt: string): string {
  // إخراج مناسب لنماذج Stable Diffusion
  const s = extractSections(prompt);
  const tags = [s.subject, s.scene, s.lighting, s.camera, s.style, s.composition, s.vfx, s.quality, s.other]
    .filter(Boolean)
    .map(t => t.replace(/\s+/g, '_').toLowerCase());
  return `${tags.join(', ')}, masterpiece, best quality, ultra realistic, 8k, photorealistic`;
}

// ─── Midjourney Style Formatter ───
function formatMidjourney(prompt: string): string {
  // إخراج مناسب لمحرك Midjourney
  const s = extractSections(prompt);
  const mjTags = [s.subject, s.scene, s.lighting, s.camera, s.style, s.composition, s.vfx, s.quality, s.other]
    .filter(Boolean)
    .map(t => t.replace(/\n/g, ' ').trim());
  return `${mjTags.join(', ')} --ar 16:9 --v 5 --q 2 --style 4c`;
}

// ─── LLM Structured Formatter ───
function formatLLMStructured(prompt: string): string {
  // إخراج منظم مناسب لنماذج اللغة
  const s = extractSections(prompt);
  return `Prompt:
Scene: ${s.scene}
Subject: ${s.subject}
Lighting: ${s.lighting}
Camera: ${s.camera}
Style: ${s.style}
Composition: ${s.composition}
VFX: ${s.vfx}
Quality: ${s.quality}
Details: ${s.other}`;
}

// ─── Node Graph Formatter ───
function formatNodeGraph(prompt: string): string {
  // إخراج المشهد كـ graph logic
  const s = extractSections(prompt);
  const nodes = [];
  if (s.scene) nodes.push(`Node(scene): ${s.scene}`);
  if (s.subject) nodes.push(`Node(subject): ${s.subject}`);
  if (s.lighting) nodes.push(`Node(lighting): ${s.lighting}`);
  if (s.camera) nodes.push(`Node(camera): ${s.camera}`);
  if (s.style) nodes.push(`Node(style): ${s.style}`);
  if (s.composition) nodes.push(`Node(composition): ${s.composition}`);
  if (s.vfx) nodes.push(`Node(vfx): ${s.vfx}`);
  if (s.quality) nodes.push(`Node(quality): ${s.quality}`);
  if (s.other) nodes.push(`Node(details): ${s.other}`);
  const edges = [];
  if (s.subject && s.scene) edges.push(`scene -> subject`);
  if (s.environment && s.lighting) edges.push(`environment -> lighting`);
  if (s.camera && s.subject) edges.push(`camera -> subject`);
  if (s.lighting && s.scene) edges.push(`lighting -> scene`);
  return `# Node Graph\n${nodes.join('\n')}\n# Relations\n${edges.join('\n')}`;
}
/**
 * Format a negative prompt in the same output style as the final prompt.
 */
export function formatNegativePrompt(negativePrompt: string, style: ExtendedPromptFormat): string {
  if (!negativePrompt.trim()) return '';

  switch (style) {
    case 'structured': {
      return `[Negative]: ${negativePrompt}`;
    }
    case 'weighted': {
      const parts = negativePrompt.split(',').map(p => p.trim()).filter(Boolean);
      return parts.map(p => `(${p}:1.0)`).join(', ');
    }
    case 'natural': {
      return `Avoid: ${negativePrompt}.`;
    }
    case 'tags': {
      return negativePrompt; // already comma-separated
    }
    case 'json': {
      const parts = negativePrompt.split(',').map(p => p.trim()).filter(Boolean);
      return JSON.stringify({ negative_prompt: parts }, null, 2);
    }
    case 'markdown': {
      const parts = negativePrompt.split(',').map(p => p.trim()).filter(Boolean);
      return `## Negative Prompt\n${parts.map(p => `- ${p}`).join('\n')}`;
    }
    case 'yaml': {
      const parts = negativePrompt.split(',').map(p => p.trim()).filter(Boolean);
      return `negative_prompt:\n${parts.map(p => `  - "${p}"`).join('\n')}`;
    }
    case 'graph': {
      const parts = negativePrompt.split(',').map(p => p.trim()).filter(Boolean).slice(0, 10);
      let id = 100;
      const nodes = parts.map(p => `[${++id}] Avoid: ${p}`);
      return `# Negative Nodes\n${nodes.join('\n')}`;
    }
    case 'bullet': {
      const parts = negativePrompt.split(',').map(p => p.trim()).filter(Boolean);
      return parts.map(p => `✗ ${p}`).join('\n');
    }
    case 'dsl': {
      const parts = negativePrompt.split(',').map(p => p.trim()).filter(Boolean);
      return parts.map(p => `exclude("${p}")`).join('\n');
    }
    default:
      return negativePrompt;
  }
}
